/**
 * Resume pending uploads (ACA-79)
 *
 * On app startup (after auth), re-drives any consult audio uploads that were
 * persisted but never completed (e.g. the upload failed and the tab was
 * reloaded). Mirrors the iOS UploadQueueManager resume behaviour so a consult
 * is not silently lost.
 *
 * Best-effort and idempotent: runs at most once per session, guards against
 * re-driving the same consult concurrently, and never throws into the caller.
 */
import { logger } from 'app/logging/Logger'
import { rehydrateRecordingBlobs } from './recordingBlobCache'

export type RetryUpload = (
    consultId: string,
    audioId: string,
    blob: Blob
) => Promise<void>

let hasRun = false
const inFlight = new Set<string>()

/** Reset the once-per-session guard (tests only). */
export function __resetResumeGuardForTests(): void {
    hasRun = false
    inFlight.clear()
}

export async function resumePendingUploads(retry: RetryUpload): Promise<void> {
    if (hasRun) return
    hasRun = true

    let records: { consultId: string; audioId: string | null; blob: Blob }[] =
        []
    try {
        records = await rehydrateRecordingBlobs()
    } catch (error) {
        logger.warn('resumePendingUploads: rehydrate failed', {
            action: 'resume_rehydrate_failed',
            metadata: { error: String(error) },
        })
        return
    }

    if (records.length === 0) return

    logger.info('resumePendingUploads: resuming pending audio uploads', {
        action: 'resume_pending_uploads',
        metadata: { count: records.length },
    })

    await Promise.all(
        records.map(async (r) => {
            // Can only resume when we know which AudioDto to attach the upload to.
            if (!r.audioId || !r.blob || r.blob.size === 0) return
            if (inFlight.has(r.consultId)) return
            inFlight.add(r.consultId)
            try {
                await retry(r.consultId, r.audioId, r.blob)
            } catch (error) {
                // Leave the persisted record in place so it can be retried again
                // on the next load (or manually from History).
                logger.warn('resumePendingUploads: resume attempt failed', {
                    action: 'resume_attempt_failed',
                    metadata: { consultId: r.consultId, error: String(error) },
                })
            } finally {
                inFlight.delete(r.consultId)
            }
        })
    )
}
